/**
 * Notifications API client
 *
 * Uses the same axios instance as the rest of the app (assumes you have a
 * configured `apiClient` like in your other API files). Adjust the import
 * line if your project uses a different axios setup.
 */
import { api } from '@/lib/api';

export type NotificationType =
  | 'info' | 'success' | 'warning' | 'error'
  | 'payment' | 'project' | 'boq' | 'document' | 'team' | 'system';

export type NotificationSeverity = 'low' | 'medium' | 'high' | 'critical';

export interface Notification {
  id: number;
  user_id: number;
  title: string;
  title_ar?: string | null;
  message: string;
  message_ar?: string | null;
  notification_type: NotificationType;
  severity: NotificationSeverity;
  link?: string | null;
  metadata_json?: Record<string, unknown> | null;
  is_read: boolean;
  read_at?: string | null;
  created_at: string;
}

export interface NotificationListResponse {
  items: Notification[];
  total: number;
  unread_count: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface ListParams {
  unread_only?: boolean;
  notification_type?: NotificationType;
  page?: number;
  page_size?: number;
}

export const notificationsApi = {
  list: async (params: ListParams = {}): Promise<NotificationListResponse> => {
    const { data } = await api.get('/notifications', { params });
    return data;
  },

  unreadCount: async (): Promise<number> => {
    const { data } = await api.get<{ unread_count: number }>(
      '/notifications/unread-count'
    );
    return data.unread_count;
  },

  get: async (id: number): Promise<Notification> => {
    const { data } = await api.get(`/notifications/${id}`);
    return data;
  },

  markRead: async (id: number): Promise<Notification> => {
    const { data } = await api.post(`/notifications/${id}/read`);
    return data;
  },

  markAllRead: async (): Promise<{ affected: number; message: string }> => {
    const { data } = await api.post('/notifications/mark-all-read');
    return data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/notifications/${id}`);
  },

  clearRead: async (): Promise<{ affected: number; message: string }> => {
    const { data } = await api.delete('/notifications/clear/read');
    return data;
  },
};
